import React from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FieldType } from 'utils/enum';
import { Layout } from 'components/Form';
import Button from 'components/Button';
import SkeletonLoader, { ContentType } from 'components/SkeletonLoader';
import { processFields, processInitialValues, processSchema } from 'utils/form';
import { readAxiosErr } from 'utils/misc';
import { emailJobDoc, useEmailJobDocFieldsConfig } from 'queries/documents';
import { useEmailRecipientsChoices } from 'queries/job';

interface IFormValues {
  recipients: number[];
  subject: string;
  message: string;
  job_docs: number[];
  attachment?: any;
}

type FormBodyProps = {
  docId: string;
  config: Record<string, any>;
  onSuccess: () => any;
};

// Inner form mounts only once `config` is available so that fields seeded from
// the config (e.g. job_docs) are present in `defaultValues` at mount time —
// JobDocSelector captures its initial value on mount and won't re-derive later.
const FormBody: React.FC<FormBodyProps> = ({ docId, config, onSuccess }) => {
  const queryClient = useQueryClient();
  const { jobId = '' } = useParams();

  const sendEmail = useMutation((payload: FormData) =>
    emailJobDoc(docId, payload)
  );

  const schema = yup.object(
    processSchema(
      {
        recipients: yup.array().of(yup.number()),
        subject: yup.string(),
        message: yup.string(),
        job_docs: yup.array().of(yup.number()),
        attachment: yup.mixed().nullable(),
      },
      config
    )
  );

  const initialValues = processInitialValues(config);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialValues,
      recipients: initialValues.recipients || [],
      job_docs: initialValues.job_docs || [],
    },
  });

  const onSubmit: SubmitHandler<IFormValues> = (data) => {
    const {
      attachment,
      recipients = [],
      job_docs = [],
      subject,
      message,
    } = data;

    const payload = new FormData();
    recipients.forEach((r) => payload.append('recipients', String(r)));
    job_docs.forEach((d) => payload.append('job_docs', String(d)));
    if (subject) payload.append('subject', subject);
    if (message) payload.append('message', message);
    const file = attachment?.[0];
    if (file) payload.append('attachment', file);

    sendEmail.mutate(payload, {
      onError: (err: any) => toast.error(readAxiosErr(err)),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['emails']),
          queryClient.invalidateQueries(['desktop-emails']),
          queryClient.invalidateQueries(['job-communications', jobId]),
        ]);
        toast.success('Email sent');
        onSuccess();
      },
    });
  };

  const fields = [
    {
      name: 'recipients',
      type: FieldType.AsyncMultiSelect,
      label: 'Recipients',
      // @ts-ignore
      error: errors.recipients?.message,
      control,
      className: 'pt-1',
      // eslint-disable-next-line react-hooks/rules-of-hooks
      optionsPromise: () => useEmailRecipientsChoices(jobId),
    },
    {
      type: FieldType.Input,
      label: 'Subject',
      placeholder: 'Email Subject',
      error: errors.subject?.message,
      ...register('subject'),
    },
    {
      type: FieldType.TextArea,
      label: 'Message',
      placeholder: 'Type here...',
      error: errors.message?.message,
      row: 8,
      ...register('message'),
    },
    {
      type: FieldType.JobDocs,
      label: 'Job Documents',
      name: 'job_docs',
      // @ts-ignore
      error: errors.job_docs?.message,
      reset: () => setValue('job_docs', []),
      job: jobId,
      control,
      initialChoices: config?.job_docs?.initial,
    },
    {
      type: FieldType.File,
      label: 'Attachment',
      boxClass: 'h-28',
      error: errors.attachment?.message,
      reset: () => setValue('attachment', null),
      ...register('attachment'),
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Layout
        fields={processFields(fields, config)}
        loading={sendEmail.isLoading}
      />
      <Button
        label={sendEmail.isLoading ? 'Sending... (Please wait)' : 'Send Email'}
        className="mt-8"
        loading={sendEmail.isLoading}
      />
    </form>
  );
};

type AppProps = {
  docId: string;
  onSuccess: () => any;
};

const Form: React.FC<AppProps> = ({ docId, onSuccess }) => {
  const { data: config, isLoading } = useEmailJobDocFieldsConfig(docId);

  if (isLoading || !config) {
    return <SkeletonLoader contentType={ContentType.Form} size={5} />;
  }

  return <FormBody docId={docId} config={config} onSuccess={onSuccess} />;
};

export default Form;
